import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginForm } from './components/auth/LoginForm';
import { AppLayout } from './components/layout/AppLayout';
import { Loading } from './components/common/Loading';
import { NotificationProvider } from './components/common/NotificationSystem';
import { OwnerConsolePage } from './components/owner-console/OwnerConsolePage';
import { useAuth } from './stores/authStore';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading fullScreen={true} message="Loading owner console..." size="large" />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={isAuthenticated ? <Navigate to="/schedule" replace /> : <LoginForm />}
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AppLayout userRole="admin" />
                  </ProtectedRoute>
                }
              >
                <Route path="dashboard" element={<Navigate to="/schedule" replace />} />
                <Route path="site" element={<OwnerConsolePage section="site" />} />
                <Route path="calendar" element={<Navigate to="/site" replace />} />
                <Route path="schedule" element={<OwnerConsolePage section="schedule" />} />
                <Route path="stripe" element={<OwnerConsolePage section="stripe" />} />
                <Route path="students" element={<OwnerConsolePage section="enrollments" />} />
                <Route path="enrollments" element={<Navigate to="/students" replace />} />
                <Route index element={<Navigate to="/schedule" replace />} />
                <Route path="*" element={<Navigate to="/schedule" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Router>
        </NotificationProvider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
